import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import api, { downloadCsv } from "@/lib/api";
import { Section, Btn, Input, Textarea, Select, Field, cardCls, StatusPill, EmptyState } from "@/components/admin/ui";
import Modal from "@/components/admin/Modal";

const TYPES = ["Lead", "Customer", "Supplier", "Partner", "Investor", "Contact", "Business Services Lead"];
const STAGES = ["New", "Contacted", "Follow-up", "Negotiating", "Won", "Lost"];
const BS_STAGES = ["New", "Discovery", "Proposal", "Follow-Up", "Negotiation", "Won", "Lost"];
const stagesFor = (type) => (type === "Business Services Lead" ? BS_STAGES : STAGES);
const EMPTY = { name: "", type: "Lead", company: "", email: "", phone: "", stage: "New", venture_id: "", follow_up_date: "", notes: "", lead_source: "", industry: "", budget_range: "", contract_value: "", monthly_value: "" };

export default function CRM() {
  const [contacts, setContacts] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/contacts", { params: typeFilter ? { type: typeFilter } : {} }).then(({ data }) => setContacts(data)).catch(() => {});
  useEffect(() => { load(); }, [typeFilter]);
  useEffect(() => { api.get("/admin/ventures").then(({ data }) => setVentures(data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setType = (e) => setForm((f) => {
    const type = e.target.value;
    const valid = stagesFor(type);
    return { ...f, type, stage: valid.includes(f.stage) ? f.stage : valid[0] };
  });
  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditing(c.id); };

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const payload = { ...form };
    if (form.type === "Business Services Lead") {
      payload.contract_value = Number(form.contract_value) || 0;
      payload.monthly_value = Number(form.monthly_value) || 0;
    } else {
      delete payload.contract_value;
      delete payload.monthly_value;
    }
    if (editing === "new") await api.post("/admin/contacts", payload);
    else await api.patch(`/admin/contacts/${editing}`, payload);
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (c) => { if (!window.confirm(`Delete ${c.name}?`)) return; await api.delete(`/admin/contacts/${c.id}`); load(); };
  const ventureName = (id) => ventures.find((v) => v.id === id)?.name || "—";

  return (
    <div data-testid="admin-crm">
      <Section
        title="Sales / CRM"
        subtitle="Leads, customers, suppliers, partners and investors — with stages and follow-ups."
        action={<div className="flex gap-3">
          <Btn variant="outline" data-testid="crm-export" onClick={() => downloadCsv("contacts", "contacts.csv")}><Download size={15} /> Export CSV</Btn>
          <Btn data-testid="crm-add" onClick={openNew}><Plus size={15} /> Add Contact</Btn>
        </div>}
      >
        <div className="flex flex-wrap gap-2 mb-6">
          {["", ...TYPES].map((t) => (
            <button key={t || "all"} data-testid={`crm-filter-${(t || "all").toLowerCase()}`} onClick={() => setTypeFilter(t)}
              className={`px-4 py-2 text-xs tracking-wider uppercase border transition-colors ${typeFilter === t ? "border-bd-gold text-bd-gold" : "border-white/15 text-bd-ivory/50 hover:text-bd-ivory"}`}>
              {t || "All"}
            </button>
          ))}
        </div>

        {contacts.length === 0 ? <EmptyState>No contacts yet.</EmptyState> : (
          <div className={`${cardCls} overflow-x-auto`}>
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="text-left text-bd-ivory/40 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-5 py-3 font-normal">Name</th><th className="px-5 py-3 font-normal">Type</th><th className="px-5 py-3 font-normal">Company</th>
                <th className="px-5 py-3 font-normal">Stage</th><th className="px-5 py-3 font-normal">Venture</th><th className="px-5 py-3 font-normal">Follow-up</th><th className="px-5 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {contacts.map((c) => (
                  <tr key={c.id} data-testid={`crm-row-${c.id}`} className="text-bd-ivory/80 hover:bg-white/[0.02]">
                    <td className="px-5 py-3"><div>{c.name}</div><div className="text-xs text-bd-ivory/35">{c.email}</div></td>
                    <td className="px-5 py-3 text-bd-ivory/60">{c.type}</td>
                    <td className="px-5 py-3 text-bd-ivory/60">{c.company || "—"}</td>
                    <td className="px-5 py-3"><StatusPill status={c.stage} /></td>
                    <td className="px-5 py-3 text-bd-ivory/60">{ventureName(c.venture_id)}</td>
                    <td className="px-5 py-3 text-bd-ivory/60">{c.follow_up_date || "—"}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(c)} className="p-2 text-bd-ivory/40 hover:text-bd-gold"><Pencil size={15} /></button>
                      <button onClick={() => remove(c)} className="p-2 text-bd-ivory/40 hover:text-red-400"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Add Contact" : "Edit Contact"} testId="crm-modal">
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name"><Input data-testid="crm-name" value={form.name} onChange={set("name")} /></Field>
            <Field label="Type"><Select data-testid="crm-type" value={form.type} onChange={setType}>{TYPES.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Company"><Input value={form.company} onChange={set("company")} /></Field>
            <Field label="Email"><Input value={form.email} onChange={set("email")} /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={set("phone")} /></Field>
            <Field label="Stage"><Select data-testid="crm-stage" value={form.stage} onChange={set("stage")}>{stagesFor(form.type).map((s) => <option key={s}>{s}</option>)}</Select></Field>
            <Field label="Associated venture"><Select value={form.venture_id || ""} onChange={set("venture_id")}><option value="">—</option>{ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</Select></Field>
            <Field label="Follow-up date"><Input type="date" value={form.follow_up_date || ""} onChange={set("follow_up_date")} /></Field>
          </div>
          {form.type === "Business Services Lead" && (
            <div data-testid="crm-bs-fields" className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-white/10 pt-5">
              <Field label="Lead source"><Input data-testid="crm-lead-source" value={form.lead_source || ""} onChange={set("lead_source")} /></Field>
              <Field label="Industry"><Input value={form.industry || ""} onChange={set("industry")} /></Field>
              <Field label="Budget range"><Input value={form.budget_range || ""} onChange={set("budget_range")} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contract value (£)"><Input data-testid="crm-contract-value" type="number" value={form.contract_value ?? ""} onChange={set("contract_value")} /></Field>
                <Field label="Monthly (£)"><Input data-testid="crm-monthly-value" type="number" value={form.monthly_value ?? ""} onChange={set("monthly_value")} /></Field>
              </div>
            </div>
          )}
          <Field label="Notes / contact history"><Textarea rows={4} value={form.notes} onChange={set("notes")} /></Field>
          <div className="flex justify-end gap-3">
            <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn data-testid="crm-save" onClick={save}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
